import { useEffect, useState } from "react";
import { CheckCircle2, Circle, Loader2, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { companion } from "@/context/CompanionAuthContext";
import { useCompanionAuth } from "@/context/CompanionAuthContext";
import { Input } from "@/components/ui/input";

const CompanionTodos = () => {
  const { user } = useCompanionAuth();
  const isAdmin = user?.role === "admin";
  const [crew, setCrew] = useState([]);
  const [ownerId, setOwnerId] = useState(user?.id);
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async (o = ownerId) => {
    setLoading(true);
    try {
      const { data } = await companion.get(`/todos?owner=${o}`);
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (isAdmin) companion.get("/crew").then((r) => setCrew(r.data.filter((u) => u.is_active !== false)));
  }, [isAdmin]);

  useEffect(() => { load(ownerId); /* eslint-disable-next-line */ }, [ownerId]);

  const add = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const payload = { title: text.trim() };
      if (isAdmin && ownerId !== user.id) payload.owner_user_id = ownerId;
      const { data } = await companion.post("/todos", payload);
      setItems((p) => [data, ...p]);
      setText("");
    } catch { toast.error("Couldn't add."); }
    finally { setBusy(false); }
  };

  const toggle = async (todo) => {
    try {
      const { data } = await companion.patch(`/todos/${todo.id}`, { done: !todo.done });
      setItems((p) => p.map((x) => (x.id === todo.id ? data : x)));
    } catch { toast.error("Couldn't update."); }
  };

  const remove = async (id) => {
    try {
      await companion.delete(`/todos/${id}`);
      setItems((p) => p.filter((x) => x.id !== id));
    } catch { toast.error("Couldn't delete."); }
  };

  return (
    <div className="px-4 py-5 space-y-4" data-testid="companion-todos-page">
      <header>
        <p className="text-xs uppercase tracking-[0.22em] text-crest-gold font-medium">To-dos</p>
        <h1 className="mt-1 font-display text-2xl font-bold text-crest-navy">
          {isAdmin && ownerId !== user.id ? `Tasks for ${crew.find((c) => c.id === ownerId)?.name || "team-mate"}` : "Your tasks"}
        </h1>
      </header>

      {isAdmin && crew.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          <Users className="h-4 w-4 text-crest-navy/60 shrink-0" />
          <button onClick={() => setOwnerId(user.id)} className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium border ${
            ownerId === user.id ? "bg-crest-green text-white border-crest-green" : "bg-white border-border text-crest-navy"
          }`} data-testid="todo-owner-mine">Mine</button>
          {crew.filter((c) => c.id !== user.id).map((c) => (
            <button key={c.id} onClick={() => setOwnerId(c.id)}
              className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium border ${
                ownerId === c.id ? "bg-crest-green text-white border-crest-green" : "bg-white border-border text-crest-navy"
              }`} data-testid={`todo-owner-${c.id}`}>
              {c.name || c.email}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={add} className="flex items-center gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a task…" className="h-11 flex-1" data-testid="todo-input" />
        <button type="submit" disabled={busy || !text.trim()} className="h-11 px-4 rounded-md bg-crest-green text-white font-semibold disabled:opacity-50 inline-flex items-center gap-1" data-testid="todo-add">
          <Plus className="h-4 w-4" /> Add
        </button>
      </form>

      {loading ? (
        <div className="grid place-items-center py-12"><Loader2 className="h-6 w-6 animate-spin text-crest-green" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-white p-6 text-center text-sm text-muted-foreground">
          Nothing on the list. Add the first one above.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((t) => (
            <li key={t.id} className="flex items-center gap-3 rounded-lg border border-border bg-white p-3" data-testid={`todo-row-${t.id}`}>
              <button onClick={() => toggle(t)} aria-label="Toggle complete" data-testid={`todo-toggle-${t.id}`}>
                {t.done
                  ? <CheckCircle2 className="h-5 w-5 text-crest-green" />
                  : <Circle className="h-5 w-5 text-crest-navy/30" />}
              </button>
              <span className={`flex-1 text-sm ${t.done ? "line-through text-muted-foreground" : "text-crest-navy"}`}>{t.title}</span>
              <button onClick={() => remove(t.id)} aria-label="Delete" data-testid={`todo-delete-${t.id}`} className="text-destructive">
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CompanionTodos;
